import { SyncWarningBadge } from '@/components/shared/SyncStatusBadge';
import { VoiceoverAudioPlayer } from '@/components/shared/VoiceoverAudioPlayer';
import { BulkTranslateTourDialog } from '@/components/tours/translations';
import type { TourTranslation } from '@/config/objects/tour-translations';
import {
  Button,
  ConfirmDialog,
  EmptyTableState,
  formatDate,
  Loading,
  RichTextContent,
  useApiClient,
  toast,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, AudioLines, FileText, Globe, Languages, Pencil, RefreshCw, Sparkles, Trash2, Volume2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  GenerateTranslationVoiceoverAction,
  RegenerateTranslationAction,
} from './actions';

/**
 * Formats a date string for display.
 */
/**
 * Tour translation detail rendered as tab content within the tour detail shell.
 * Matches the card-based layout of TourOverviewTab.
 */
export function TourTranslationDetailPage() {
  const { id: tourId, translationId } = useParams<{ id: string; translationId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const [generateVoiceoverModalOpen, setGenerateVoiceoverModalOpen] = useState(false);
  const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);
  const [catchUpDialogOpen, setCatchUpDialogOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data, isLoading } = useQuery<{ success: boolean; data: TourTranslation }>({
    queryKey: ['TourTranslation', translationId],
    queryFn: () => apiClient.get(`/tours/${tourId}/translations/${translationId}`),
    enabled: !!tourId && !!translationId,
  });

  const translation = data?.data;

  const deleteMutation = useMutation({
    mutationFn: async (id: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/tours/${tourId}/translations/${id}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['Translation'] });
        navigate(`/tours/${tourId}/translations`);
      } else {
        throw new Error(response.error || 'Failed to delete translation');
      }
    },
  });

  const handleDelete = () => {
    if (!translation) return;
    toast.promise(deleteMutation.mutateAsync(translation.id), {
      loading: 'Deleting translation...',
      success: 'Translation deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete translation',
    });
  };

  const openVoiceoverDialog = () => setGenerateVoiceoverModalOpen(true);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loading />
      </div>
    );
  }

  if (!tourId || !translationId || !translation) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">Translation Not Found</h2>
        <p className="text-muted-foreground">The requested translation could not be found.</p>
      </div>
    );
  }

  // Fields whose source content has drifted since this translation was made.
  // These drive the "Sync with source" action and the dialog's field summary.
  const outdatedFields: string[] = [];
  if (translation.isNameOutdated) outdatedFields.push('Name');
  if (translation.isTaglineOutdated) outdatedFields.push('Tagline');
  if (translation.isDescriptionOutdated) outdatedFields.push('Description');
  if (translation.isConclusionOutdated) outdatedFields.push('Conclusion');
  const isOutdated = outdatedFields.length > 0;
  // Only description/conclusion have voiceovers, so only offer voiceover
  // regeneration when one of those drifted.
  const canRegenerateVoiceovers = Boolean(
    translation.isDescriptionOutdated || translation.isConclusionOutdated,
  );

  return (
    <div className="space-y-6">
      {/* Action bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-3">
          <Button
            variant={isOutdated ? 'default' : 'outline'}
            onClick={() => setRegenerateModalOpen(true)}
          >
            <RefreshCw className="h-4 w-4" />
            {isOutdated ? 'Sync with source' : 'Regenerate translation'}
          </Button>
          <Button
            variant="outline"
            onClick={openVoiceoverDialog}
          >
            <AudioLines className="h-4 w-4" />
            Generate Voiceovers
          </Button>
          {translation.language && (
            <Button
              variant="outline"
              onClick={() => setCatchUpDialogOpen(true)}
            >
              <Languages className="h-4 w-4" />
              Catch Up Missing Attractions
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => navigate(`/tours/${tourId}/translations/${translationId}/edit`)}>
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
          <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left column */}
        <div className="lg:col-span-3 space-y-6">
          {/* Description card */}
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-medium tracking-tight text-zinc-900">Description</h2>
                <SyncWarningBadge
                  isInSync={
                    translation.isDescriptionOutdated === true
                      ? false
                      : translation.isDescriptionOutdated === false
                        ? true
                        : null
                  }
                  message="Source tour description has changed since this translation was generated"
                />
              </div>
              <p className="text-sm text-muted-foreground">Translated tour description</p>
            </div>
            <div className="p-6 space-y-6">
              {translation.tagline && (
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-2">
                    Tagline
                    <SyncWarningBadge
                      isInSync={
                        translation.isTaglineOutdated === true
                          ? false
                          : translation.isTaglineOutdated === false
                            ? true
                            : null
                      }
                      message="Source tour tagline has changed since this translation was generated"
                    />
                  </div>
                  <p className="text-sm italic text-foreground">{translation.tagline}</p>
                </div>
              )}

              {/* Voiceover above description */}
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Volume2 className="h-3.5 w-3.5 shrink-0" />
                  Voiceover
                </div>
                {translation.voiceover ? (
                  <VoiceoverAudioPlayer
                    voiceover={translation.voiceover}
                    isInSync={translation.isDescriptionVoiceoverInSync ?? null}
                    outOfSyncMessage="Description has changed since voiceover was generated"
                  />
                ) : (
                  <div className="flex items-center justify-between rounded-lg border border-dashed px-4 py-3">
                    <p className="text-sm text-muted-foreground">No voiceover generated</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openVoiceoverDialog}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Generate
                    </Button>
                  </div>
                )}
              </div>

              {/* Description text */}
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                  Content
                </div>
                {translation.description ? (
                  <RichTextContent content={translation.description} />
                ) : (
                  <EmptyTableState
                    icon={<FileText className="h-6 w-6" />}
                    title="No description"
                    description="Edit to add a translated description"
                  />
                )}
              </div>
            </div>
            <div className="border-t px-6 py-4 text-sm text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
              <span>Created: {formatDate(translation.createdAt)}</span>
              <span>Updated: {formatDate(translation.updatedAt)}</span>
            </div>
          </div>

          {/* Conclusion card */}
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-medium tracking-tight text-zinc-900">Conclusion</h2>
                <SyncWarningBadge
                  isInSync={
                    translation.isConclusionOutdated === true
                      ? false
                      : translation.isConclusionOutdated === false
                        ? true
                        : null
                  }
                  message="Source tour conclusion has changed since this translation was generated"
                />
              </div>
              <p className="text-sm text-muted-foreground">Translated closing text shown at the end of the tour</p>
            </div>
            <div className="p-6 space-y-6">
              {/* Conclusion Voiceover */}
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Volume2 className="h-3.5 w-3.5 shrink-0" />
                  Voiceover
                </div>
                {translation.conclusionVoiceover ? (
                  <VoiceoverAudioPlayer
                    voiceover={translation.conclusionVoiceover}
                    isInSync={translation.isConclusionVoiceoverInSync ?? null}
                    outOfSyncMessage="Conclusion has changed since voiceover was generated"
                  />
                ) : (
                  <div className="flex items-center justify-between rounded-lg border border-dashed px-4 py-3">
                    <p className="text-sm text-muted-foreground">No voiceover generated</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openVoiceoverDialog}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Generate
                    </Button>
                  </div>
                )}
              </div>

              {/* Conclusion text */}
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                  Content
                </div>
                {translation.conclusion ? (
                  <RichTextContent content={translation.conclusion} />
                ) : (
                  <EmptyTableState
                    icon={<FileText className="h-6 w-6" />}
                    title="No conclusion"
                    description="Edit to add a translated conclusion"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Translation Info card */}
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">Translation Info</h2>
              <p className="text-sm text-muted-foreground">Language and sync status</p>
            </div>
            <div className="p-6">
              <div className="divide-y">
                <div className="py-3 first:pt-0">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                    <Globe className="h-3.5 w-3.5 shrink-0" />
                    Language
                  </div>
                  <div className="text-sm">
                    {translation.language ? (
                      <span>
                        {translation.language.name}{' '}
                        <span className="text-muted-foreground">({translation.language.code})</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Unknown</span>
                    )}
                  </div>
                </div>
                <div className="py-3">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                    <Languages className="h-3.5 w-3.5 shrink-0" />
                    Translated Name
                  </div>
                  <div className="text-sm flex items-center gap-2">
                    {translation.name}
                    <SyncWarningBadge
                      isInSync={
                        translation.isNameOutdated === true
                          ? false
                          : translation.isNameOutdated === false
                            ? true
                            : null
                      }
                      message="Source tour name has changed since this translation was generated"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sync Status card */}
          {(translation.isNameOutdated || translation.isDescriptionOutdated || translation.isTaglineOutdated || translation.isConclusionOutdated) && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 shadow-sm">
              <div className="px-6 py-4 border-b border-amber-200">
                <h2 className="text-lg font-medium tracking-tight text-amber-900">Sync Status</h2>
                <p className="text-sm text-amber-700">Some content may be outdated</p>
              </div>
              <div className="p-6">
                <div className="space-y-2">
                  {translation.isNameOutdated && (
                    <div className="flex items-center gap-2 text-sm text-amber-800">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      Name is outdated
                    </div>
                  )}
                  {translation.isDescriptionOutdated && (
                    <div className="flex items-center gap-2 text-sm text-amber-800">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      Description is outdated
                    </div>
                  )}
                  {translation.isTaglineOutdated && (
                    <div className="flex items-center gap-2 text-sm text-amber-800">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      Tagline is outdated
                    </div>
                  )}
                  {translation.isConclusionOutdated && (
                    <div className="flex items-center gap-2 text-sm text-amber-800">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      Conclusion is outdated
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Modals */}
      <RegenerateTranslationAction
        tourId={tourId}
        translationId={translationId}
        open={regenerateModalOpen}
        onOpenChange={setRegenerateModalOpen}
        outdatedFields={outdatedFields}
        canRegenerateVoiceovers={canRegenerateVoiceovers}
      />

      <GenerateTranslationVoiceoverAction
        tourId={tourId}
        translationId={translationId}
        open={generateVoiceoverModalOpen}
        onOpenChange={setGenerateVoiceoverModalOpen}
      />

      {translation.language && (
        <BulkTranslateTourDialog
          tourId={tourId}
          open={catchUpDialogOpen}
          onClose={() => setCatchUpDialogOpen(false)}
          presetLanguage={{
            id: translation.language.id,
            name: translation.language.name,
          }}
        />
      )}

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Translation"
        description={`Are you sure you want to delete this ${translation.language?.name || ''} translation? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
